/**
 * service-mail draft client. Mirrors:
 *   apps/service-mail/src/modules/draft/draft.controller.ts
 *
 * The submit endpoint may return 200 (success) or 422 (prohibited items —
 * hard or soft). We surface the 422 body as a typed result so the UI can
 * pattern-match without hand-decoding error payloads.
 */
import { env } from '../env';

import { ApiError, request } from './client';
import type {
  CreateDraftRequest,
  DraftResponse,
  ListDraftsQuery,
  ListDraftsResponse,
  ProhibitedItemHit,
  SubmitDraftResult,
  SubmitProhibitedHardFailure,
  SubmitSoftWarningFailure,
  UpdateDraftRequest,
} from './types';

const baseUrl = (): string => env.serviceMailBaseUrl;

export async function createDraft(dto: CreateDraftRequest): Promise<DraftResponse> {
  return request<DraftResponse>(baseUrl(), '/v1/drafts', {
    method: 'POST',
    body: dto,
  });
}

export async function getDraft(id: string): Promise<DraftResponse> {
  return request<DraftResponse>(baseUrl(), `/v1/drafts/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

export async function updateDraft(id: string, dto: UpdateDraftRequest): Promise<DraftResponse> {
  return request<DraftResponse>(baseUrl(), `/v1/drafts/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: dto,
  });
}

export async function listDrafts(query: ListDraftsQuery = {}): Promise<ListDraftsResponse> {
  return request<ListDraftsResponse>(baseUrl(), '/v1/drafts', {
    method: 'GET',
    query: {
      status: query.status,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

interface SubmitDraftBody {
  acknowledgedSoftWarnings?: boolean;
}

export async function submitDraft(
  id: string,
  body: SubmitDraftBody = {},
): Promise<SubmitDraftResult> {
  try {
    return await request<SubmitDraftResult>(baseUrl(), `/v1/drafts/${encodeURIComponent(id)}/submit`, {
      method: 'POST',
      body,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 422) {
      const failure = parseSubmitFailure(err.body);
      if (failure !== null) {
        return failure;
      }
    }
    throw err;
  }
}

function parseSubmitFailure(
  body: unknown,
): SubmitProhibitedHardFailure | SubmitSoftWarningFailure | null {
  if (typeof body !== 'object' || body === null) {
    return null;
  }
  const record = body as Record<string, unknown>;
  // Nest's UnprocessableEntityException wraps under `message` sometimes.
  // We try both shapes.
  const candidate = (record['reason'] !== undefined ? record : record['message']) as
    | Record<string, unknown>
    | undefined;
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }
  const reason = (candidate as Record<string, unknown>)['reason'];
  const hitsRaw = (candidate as Record<string, unknown>)['hits'];

  if (
    reason !== 'PROHIBITED_ITEMS_BLOCKED' &&
    reason !== 'SOFT_WARNINGS_NEED_ACKNOWLEDGEMENT'
  ) {
    return null;
  }

  const hits = Array.isArray(hitsRaw)
    ? (hitsRaw.filter((h) => typeof h === 'object' && h !== null) as ProhibitedItemHit[])
    : [];

  if (reason === 'PROHIBITED_ITEMS_BLOCKED') {
    return { ok: false, reason: 'PROHIBITED_ITEMS_BLOCKED', hits };
  }
  return { ok: false, reason: 'SOFT_WARNINGS_NEED_ACKNOWLEDGEMENT', hits };
}
